import {useEffect} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import type {CalculatorTier} from 'client/Assignments/constants';

import type {CalculatorType} from './CalculatorTitleBar.react';
import {calculatorTypesFor} from './calculatorTier';

/**
 * How the calculator window occupies the page (Student Tools Figma, "Pin &
 * Dock", node 3309:28554):
 * - `floating` — a draggable window over the question
 * - `docked` — a full-height rail on the trailing edge; the page shrinks beside it
 * - `expanded` — fills the viewport
 *
 * All three are CSS states of one container that never unmounts, so switching
 * between them keeps the student's in-progress work. Note that `expanded` is
 * deliberately not the Fullscreen API: assignment practice runs Secure Mode,
 * which treats every `fullscreenchange` as a violation.
 */
export type CalculatorWindowMode = 'floating' | 'docked' | 'expanded';

/**
 * Width of the docked calculator, published so a host page can shrink itself
 * beside it. Exported as a pair (the property and the class that reads it) so
 * the name lives in one type-checked place rather than being retyped per host.
 * The window sizes itself from the same property, so the two cannot disagree.
 */
export const CALCULATOR_DOCK_INLINE_SIZE_VAR = '--calculator-dock-inline-size';
export const CALCULATOR_DOCK_MARGIN_CLASS = 'me-[var(--calculator-dock-inline-size,0px)]';

export interface CalculatorWindowPosition {
  x: number;
  y: number;
}

export interface CalculatorWindowState {
  isOpen: boolean;
  type: CalculatorType;
  mode: CalculatorWindowMode;
  /** Where leaving `expanded` returns to; never `expanded` itself. */
  previousMode: Exclude<CalculatorWindowMode, 'expanded'>;
  /** Floating-mode offset from the window's default resting spot, in px. */
  position: CalculatorWindowPosition;
  /** Docked-mode width the student dragged to, in px; `null` means the type's default. */
  dockedWidth: number | null;
  /**
   * Calculators the student has selected at least once this session. Each one
   * is a separate lazily-loaded chunk, so they mount on first selection rather
   * than all at once, and stay mounted afterwards to keep their work.
   */
  mountedTypes: CalculatorType[];
  /**
   * The most capable calculator the current question allows, set by the practice view hosting the
   * window. `none` hides the calculator, and it is where a session starts until the host sets one.
   */
  tier: CalculatorTier;
}

interface CalculatorWindowActions {
  close: () => void;
  toggle: () => void;
  setType: (type: CalculatorType) => void;
  setMode: (mode: CalculatorWindowMode) => void;
  setPosition: (position: CalculatorWindowPosition) => void;
  setDockedWidth: (width: number) => void;
  setTier: (tier: CalculatorTier) => void;
  reset: () => void;
}

const initialState: CalculatorWindowState = {
  isOpen: false,
  type: 'graphing',
  mode: 'floating',
  previousMode: 'floating',
  position: {x: 0, y: 0},
  dockedWidth: null,
  mountedTypes: [],
  tier: 'none'
};

/** `type` if the tier allows it, otherwise the most capable calculator the tier does allow. */
const typeWithin = (type: CalculatorType, tier: CalculatorTier): CalculatorType => {
  const allowed = calculatorTypesFor(tier);
  return allowed.length === 0 || allowed.includes(type) ? type : allowed[allowed.length - 1];
};

export const useCalculatorWindowStore = create<CalculatorWindowState & CalculatorWindowActions>()(
  devtools(
    (set) => ({
      ...initialState,

      // Hiding only. The student's work survives a close and reopen within the
      // same question; the question and session boundaries call reset().
      close: () => {
        set({isOpen: false}, false, 'close');
      },

      toggle: () => {
        set(
          (state) =>
            state.isOpen
              ? {isOpen: false}
              : {
                  isOpen: true,
                  mode: state.mode === 'expanded' ? state.previousMode : state.mode,
                  mountedTypes: state.mountedTypes.includes(state.type)
                    ? state.mountedTypes
                    : [...state.mountedTypes, state.type]
                },
          false,
          'toggle'
        );
      },

      // Both of these reset the drag offset, because both change the box the
      // offset was measured against. The window is anchored to the trailing
      // edge and the two calculators are different widths, so keeping an offset
      // across a type switch can throw the window clean off the screen.
      setType: (type: CalculatorType) => {
        set(
          (state) => ({
            type,
            position: initialState.position,
            dockedWidth: initialState.dockedWidth,
            mountedTypes: state.mountedTypes.includes(type)
              ? state.mountedTypes
              : [...state.mountedTypes, type]
          }),
          false,
          'setType'
        );
      },

      // `dockedWidth` survives: it is measured against the viewport, not the
      // window's resting box, so a trip through floating does not invalidate it.
      setMode: (mode: CalculatorWindowMode) => {
        set(
          (state) => ({
            mode,
            previousMode: state.mode === 'expanded' ? state.previousMode : state.mode,
            position: initialState.position
          }),
          false,
          'setMode'
        );
      },

      setPosition: (position: CalculatorWindowPosition) => {
        set({position}, false, 'setPosition');
      },

      setDockedWidth: (width: number) => {
        set({dockedWidth: width}, false, 'setDockedWidth');
      },

      setTier: (tier: CalculatorTier) => {
        set(
          (state) => {
            const allowed = calculatorTypesFor(tier);
            if (allowed.length === 0) {
              return {tier, isOpen: false, mountedTypes: []};
            }
            const type = typeWithin(state.type, tier);
            const mountedTypes = state.mountedTypes.filter((mounted) => allowed.includes(mounted));
            return {
              tier,
              type,
              // An open window has to have the calculator it now shows mounted.
              mountedTypes:
                state.isOpen && !mountedTypes.includes(type)
                  ? [...mountedTypes, type]
                  : mountedTypes,
              ...(type !== state.type && {
                position: initialState.position,
                dockedWidth: initialState.dockedWidth
              })
            };
          },
          false,
          'setTier'
        );
      },

      // Discards the student's work but keeps what the question allows, which only its host sets.
      reset: () => {
        set(
          (state) => ({
            ...initialState,
            tier: state.tier,
            type: typeWithin(initialState.type, state.tier)
          }),
          false,
          'reset'
        );
      }
    }),
    {name: 'CalculatorWindowStore'}
  )
);

/**
 * Clears the window on mount and unmount. Call this at the practice-session
 * level: the store is a module singleton, so without it a student who left one
 * session with the calculator docked would find it docked again on the next,
 * carrying a position measured against a page that is no longer there.
 */
export function useCalculatorWindowReset(): void {
  const reset = useCalculatorWindowStore((state) => state.reset);

  useEffect(() => {
    reset();
    return reset;
  }, [reset]);
}
